import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { createStory, submitStory, submitStoryVoice } from "../api/stories";
import { AudioRecorder } from "../components/AudioRecorder";
import { InkDivider } from "../components/InkDivider";
import { RisographBackground } from "../components/RisographBackground";
import { getInputModePref, saveInputModePref } from "../hooks/useInputModePref";

type InputMode = "text" | "voice";

interface LocationState {
	promptContext?: string;
	inputMode?: InputMode;
}

export function NewStoryPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const location = useLocation();
	const locationState = location.state as LocationState | null;
	const promptContext = locationState?.promptContext;
	const [title, setTitle] = useState("");
	const [body, setBody] = useState("");
	const [inputMode, setInputMode] = useState<InputMode>(
		locationState?.inputMode ?? getInputModePref(),
	);
	const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pendingStoryId, setPendingStoryId] = useState<string | null>(null);

	const canSubmit =
		inputMode === "text" ? body.trim().length > 0 : audioBlob !== null;

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		if (!canSubmit) return;

		setLoading(true);
		setError(null);
		try {
			const storyId =
				pendingStoryId ??
				(await createStory(title.trim() || undefined).then((s) => {
					setPendingStoryId(s.id);
					return s.id;
				}));
			if (inputMode === "text") {
				await submitStory(storyId, body.trim());
			} else if (audioBlob) {
				await submitStoryVoice(storyId, audioBlob);
			}
			saveInputModePref(inputMode);
			navigate(`/stories/${storyId}`);
		} catch {
			setError(t("stories.new.errorGeneric"));
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<button
				type="button"
				onClick={() => navigate(-1)}
				className="font-ui text-xs uppercase tracking-widest text-ink/50 hover:text-ink/80 transition-colors self-start mb-10 py-2 -my-2"
			>
				{t("stories.new.back")}
			</button>

			<form onSubmit={handleSubmit} className="flex flex-col flex-1">
				<div className="mb-5">
					<input
						type="text"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder={t("stories.new.titlePlaceholder")}
						className="w-full bg-transparent font-display text-3xl font-bold text-ink placeholder-ink/25 outline-none leading-tight"
					/>
				</div>

				<div className="flex gap-6 mb-1">
					<button
						type="button"
						onClick={() => setInputMode("text")}
						className={`font-ui text-[11px] uppercase tracking-widest transition-colors pb-1 border-b ${
							inputMode === "text"
								? "text-ink border-ink"
								: "text-ink/30 border-transparent hover:text-ink/55"
						}`}
					>
						{t("stories.new.modeText")}
					</button>
					<button
						type="button"
						onClick={() => setInputMode("voice")}
						className={`font-ui text-[11px] uppercase tracking-widest transition-colors pb-1 border-b ${
							inputMode === "voice"
								? "text-ink border-ink"
								: "text-ink/30 border-transparent hover:text-ink/55"
						}`}
					>
						{t("stories.new.modeVoice")}
					</button>
				</div>

				<InkDivider className="mb-8" />

				{promptContext && (
					<div className="mb-8 pl-4 border-l-2 border-blue bg-ink/[0.03] py-4 pr-4">
						<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-2">
							Today's prompt
						</p>
						<p className="font-body italic text-base text-ink/70 leading-relaxed">
							{promptContext}
						</p>
					</div>
				)}

				<div className="flex-1">
					{inputMode === "voice" ? (
						<div key="voice" className="animate-fade-in flex flex-col gap-6">
							<p className="font-body italic text-ink/50 text-lg leading-relaxed">
								{t("stories.new.voicePrompt")}
							</p>
							<AudioRecorder
								onRecordingComplete={(blob) => setAudioBlob(blob)}
								onReset={() => setAudioBlob(null)}
							/>
						</div>
					) : (
						<div key="text" className="animate-fade-in">
							<textarea
								value={body}
								onChange={(e) => setBody(e.target.value)}
								placeholder={t("stories.new.writePrompt")}
								className="w-full min-h-[320px] bg-transparent font-body italic text-ink/80 text-lg placeholder-ink/30 outline-none resize-none leading-relaxed"
							/>
						</div>
					)}
				</div>

				{error && (
					<p className="font-ui text-sm uppercase tracking-wide text-coral mt-4">
						{error}
					</p>
				)}

				<InkDivider className="mt-8" />

				<div className="pt-6 pb-8">
					<button
						type="submit"
						disabled={!canSubmit || loading}
						className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
					>
						{loading
							? t("stories.new.submitLoading")
							: t("stories.new.submitIdle")}
					</button>
				</div>
			</form>
		</div>
	);
}
